// AuthenticatedApp.js
import React, { useEffect, useState } from 'react';
import SideMenu from '../components/SideMenu';
import ResponsesContainer from '../components/ResponsesContainer';
import UserInputContainer from '../components/UserInputContainer';
import styled from '@xstyled/styled-components';
import { Widths } from '../common/Constants';
import AppHeader from '../components/AppHeader';
import { useMsal } from '@azure/msal-react';
import { DefaultUserInput } from '../common/Constants';
import { useCharacterHandlers } from '../hooks/useCharacterHandlers';
import useResponseHistory from '../hooks/useResponseHistory';

import ConfirmationModal from '../components/ConfirmationModal';
import EditConfigModal from '../components/EditConfigModal';
import AboutModal from '../components/AboutModal';
import ContactModal from '../components/ContactModal';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const VisibleContentContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  flex-grow: 1;
  overflow: hidden;

  @media (max-width: ${Widths.WIDTH_700}) {
    align-items: flex-start;
    padding: 0px clamp(2px, 1vw, 10px);
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100vh;
  padding-left: clamp(1px, 1vw, 10px);
  overflow-y: auto;
`;

function AuthenticatedApp({
  version
}) {
  const { instance, accounts } = useMsal();
  const [isFakeResponse, setIsFakeResponse] = useState(false);
  const [userInput, setUserInput] = useState(String(DefaultUserInput));
  const [showAbout, setShowAbout] = useState(false);
  const [showContact, setShowContact] = useState(false);

  const {
    aiCharactersConfig,
    editingConfig,
    setEditingConfig,
    showEditConfigModal,
    showConfirmationModal,
    handleDeleteCharacter,
    handleSaveEditedCharacter,
    handleAddNewCharacter,
    setShowEditConfigModal,
    setShowConfirmationModal,
    handleResetToDefaults,
    handleImportNewCharactersConfig,
    characterNameExists,
    loadConfig,
    isLoading
  } = useCharacterHandlers();

  const { responseHistory, addNewResponse, summarizeResponses, setResponseHistory } = useResponseHistory();

  useEffect(() => {
    ////console.debug('AuthenticatedApp: instance: ', JSON.stringify(instance));
    const getAccessToken = async () => {
      loadConfig();
    };

    getAccessToken();
  }, [instance, accounts, loadConfig]);

  const handleConfirmResetToDefaults = () => {
    handleResetToDefaults();
    setShowConfirmationModal(false);
  };

  const handleCancelResetToDefaults = () => {
    setShowConfirmationModal(false);
  };

  const isEditingMode = (configName) => {
    return characterNameExists(configName);
  };

  const handleShowAbout = () => setShowAbout(true);
  const handleShowContact = () => setShowContact(true);

  return (
    <AppContainer>
      <AppHeader showAboutModal={handleShowAbout} showContactModal={handleShowContact} />

      <VisibleContentContainer>
        <SideMenu
          aiCharactersConfig={aiCharactersConfig}
          setEditingConfig={setEditingConfig}
          setShowEditConfigModal={setShowEditConfigModal}
          resetToDefaults={handleConfirmResetToDefaults}
          addNewCharacter={handleAddNewCharacter}
          addNewResponse={addNewResponse}
          deleteCharacter={handleDeleteCharacter}
          summarizeResponses={summarizeResponses}
          setIsFakeResponse={setIsFakeResponse}
          isFakeResponse={isFakeResponse}
          userInput={userInput}
          version={version}
          importCharacterConfig={handleImportNewCharactersConfig}
          isLoading={isLoading}
        />
        <ContentArea>
          <UserInputContainer
            userInput={userInput}
            setUserInput={setUserInput}
          />
          <ResponsesContainer
            responseHistory={responseHistory}
            setResponseHistory={setResponseHistory}
          />
        </ContentArea>
      </VisibleContentContainer>
      {showConfirmationModal && (
        <ConfirmationModal
          message="Are you sure you want to reset the configuration to defaults? This action cannot be undone."
          buttons={[
            ['Yes', handleConfirmResetToDefaults, 'yesButton'],
            ['No', handleCancelResetToDefaults, 'noButton']
          ]}
        />
      )}
      {showEditConfigModal && (
        <EditConfigModal
          characterConfig={editingConfig}
          isEditingMode={isEditingMode}
          onDelete={handleDeleteCharacter}
          onSave={handleSaveEditedCharacter}
          onClose={() => setShowEditConfigModal(false)}
        />
      )}

      <AboutModal show={showAbout} onClose={() => setShowAbout(false)} />
      <ContactModal show={showContact} onClose={() => setShowContact(false)} />
    </AppContainer>
  );
}

export default AuthenticatedApp;
